import React from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTheme } from "@mui/material/styles";
import styled from "styled-components";
import { colors, fonts } from "../../common/mixins";
import { ProductInfoSection, ProductMedia, ProductPrice } from "./ProductCard";
import { StoreProduct } from "../../../stores/useReservationStore";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import {
  GradientText,
  ScrollEndSentinel,
  useElementRef,
  useIsVisibleIn,
} from "./scrollFade";

const DrawerContent = styled.div`
  padding: 16px 16px 24px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const DrawerPuller = styled.div`
  width: 48px;
  height: 4px;
  border-radius: 3px;
  background: ${colors.pullerColor};
  margin: 0 auto 16px auto;
  flex-shrink: 0;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 24px;
`;

const DrawerBodyWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  overflow: hidden;
  position: relative;
`;

const DrawerBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DrawerFooter = styled.div`
  flex-shrink: 0;
  padding-top: 4px;
  gap: 24px;
  display: flex;
  flex-direction: column;
`;

const DrawerTitle = styled.div`
  ${fonts.midtitle()}
`;

const DrawerMediaAndInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface ProductDrawerProps {
  open: boolean;
  onClose: () => void;
  product: StoreProduct | null;
  /** Add button / options popup, rendered by Products (it owns that state). */
  actions: React.ReactNode;
}

// Mobile product details: opened by tapping a product card.
export function ProductDrawer({
  open,
  onClose,
  product,
  actions,
}: ProductDrawerProps) {
  const r = useReservationResources();
  const theme = useTheme();

  const [scrollNode, scrollRef] = useElementRef<HTMLDivElement>();
  const [endNode, scrollEndRef] = useElementRef<HTMLDivElement>();
  const [descriptionNode, descriptionRef] =
    useElementRef<HTMLParagraphElement>();
  const isScrollEndVisible = useIsVisibleIn(scrollNode, endNode);
  const isDescriptionVisible = useIsVisibleIn(scrollNode, descriptionNode);

  const productId = product?.id ?? null;
  React.useEffect(() => {
    if (open && scrollNode) {
      scrollNode.scrollTop = 0;
    }
  }, [open, productId, scrollNode]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      disableSwipeToOpen
      disableScrollLock // page scroll is locked by useModal
      sx={{ zIndex: theme.zIndex.modal }}
      PaperProps={{
        style: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      {product && (
        <DrawerContent>
          <DrawerPuller />
          <DrawerBodyWrapper>
            <DrawerBody ref={scrollRef}>
              <DrawerHeader>
                <DrawerTitle>{r.SelectProduct}</DrawerTitle>
              </DrawerHeader>
              <DrawerMediaAndInfo>
                <ProductMedia product={product} />
                <ProductInfoSection
                  product={product}
                  showPrice={false}
                  descriptionRef={descriptionRef}
                />
              </DrawerMediaAndInfo>
              <ScrollEndSentinel ref={scrollEndRef} />
            </DrawerBody>
            <GradientText $hidden={!isDescriptionVisible || isScrollEndVisible} />
          </DrawerBodyWrapper>
          <DrawerFooter>
            <ProductPrice options={product.productOptions} />
            {actions}
          </DrawerFooter>
        </DrawerContent>
      )}
    </SwipeableDrawer>
  );
}
